import { mapGetters, mapActions } from "vuex";

import _ from "lodash";
import UsersApi from "@/common/http/UsersApi";
import AtsStages from "@/components/AtsStages/Component";
import TalentWall from "@/components/TalentWall/Component";
import AtsLogo from "@/components/AtsLogo/AtsLogo";
import { loadScript as loadMergeDevScript, initialize as initializeMergeDev, openLink as openMergeDevLink } from "@/common/MergeDev";
import AtsApi from "@/common/http/AtsApi";

// The OAuth callback forwards an error *code*, never free text. Anything we don't
// recognize falls back to generic copy: the param is reachable by anyone who can
// hand someone a link, and this alert renders on a credentials page.
const GREENHOUSE_OAUTH_ERROR_FALLBACK = "Could not connect Greenhouse. Please try again.";
const GREENHOUSE_OAUTH_ERRORS = {
  access_denied: "Greenhouse access was declined. Connect again and approve the requested permissions.",
  invalid_scope: "Greenhouse did not grant all the permissions Teamable needs. Please contact support.",
  insufficient_permissions: "The Greenhouse user who authorized isn't a Site Admin, so Teamable can't read your data. Reconnect with a Greenhouse Site Admin account.",
  connect_failed: GREENHOUSE_OAUTH_ERROR_FALLBACK,
};

export default {
  name: "AtsConnect",
  components: {
    AtsStages,
    AtsLogo,
    TalentWall,
  },
  props: {
    requireAts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      keyValid: false,
      stagesSelected: false,
      atsName: null,
      // Whether the team already had a v1 Harvest key when this screen loaded.
      // Captured once on mount rather than read from settings.greenhouseApiKey,
      // which Change/Cancel clears mid-edit — reading it live would make the key
      // block disappear while someone is editing it.
      hadExistingGreenhouseKey: false,
      atsGreenhouseKeyCheck: {
        isLoading: false,
        success: null,
        status: null,
        description: null,
        originalKey: null,
        isEditing: false,
      },
      atsLeverKeyCheck: {
        isLoading: false,
        success: null,
        status: null,
        description: null,
        originalKey: null,
        isEditing: false,
      },
      leverIntegrationGuideLink: "https://support.teamable.com/article/714-integrating-lever-with-teamable",
      greenhouseIntegrationGuideLink: "https://support.teamable.com/article/59-integrating-greenhouse-with-teamable",
      greenhouseV3Loading: false,
      greenhouseV3Message: null,
      greenhouseV3MessageType: "success",
    };
  },
  computed: {
    ...mapGetters({
      user: "currentUser",
      isInboundsEnabled: "isInboundsEnabled",
      settings: "currentUserSettings",
    }),
    atsNameComputed: {
      get() {
        return this.atsName || this.user.primaryTeamAtsName || "None";
      },
      set(val) {
        this.atsName = val;
      },
    },
    showGreenhouseTab() {
      return (
        (this.user.primaryTeamAtsName === "Greenhouse" && this.user.primaryTeamAtsName === this.atsNameComputed) ||
        (this.atsNameComputed === "Greenhouse" && !this.user.primaryTeamAtsName)
      );
    },
    showLeverTab() {
      return (
        (this.user.primaryTeamAtsName === "Lever" && this.user.primaryTeamAtsName === this.atsNameComputed) ||
        (this.atsNameComputed === "Lever" && !this.user.primaryTeamAtsName)
      );
    },
    showMergeAtsTab() {
      return (
        this.settings.mergeDevApiKey &&
        this.user.primaryTeamAtsName !== "Greenhouse" && // Because it's already there
        this.user.primaryTeamAtsName !== "Lever" // Because it's already there
      );
    },
    submitButtonDisabled() {
      return !["Finish", "Save", "OK"].includes(this.submitButtonLabel);
    },
    showSubmitButton() {
      if (this.atsName === "None" && !this.user.primaryTeamAtsName) {
        return true;
      }
      if (this.requireAts) {
        return this.submitButtonLabel === "OK" || this.submitButtonLabel === "Finish";
      }
      return false;
    },
    // The v1 Harvest key stays in the DB as a support-side rollback valve (the admin
    // page keeps its field), but a customer manages exactly one live connection: once
    // the team is connected over OAuth, the key block goes away and there is nothing
    // here that tests v1. The server refuses those endpoints for v3 teams too.
    showGreenhouseKeyBlock() {
      if (this.settings.greenhouseV3Connected) {
        return false;
      }
      return this.hadExistingGreenhouseKey || this.atsGreenhouseKeyCheck.isEditing;
    },
    showGreenhouseCancelButton() {
      return this.atsGreenhouseKeyCheck.isEditing && !this.requireAts;
    },
    showLeverCancelButton() {
      return this.atsLeverKeyCheck.isEditing && !this.requireAts;
    },
    submitButtonLabel() {
      if (this.atsName === "None" && !this.requireAts) {
        return "Save";
      }

      if (!this.atsName) {
        return "Choose an ATS";
      }

      if (!this.keyValid) {
        return "Enter an API key";
      }

      return "Finish";
    },
  },
  watch: {
    atsName: "resetAtsKeyCheck",
    atsNameComputed: {
      handler(newValue, oldValue) {
        if (newValue === "Other" && !this.settings.mergeDevApiKey && !this.user.primaryTeamAtsName) {
          openMergeDevLink();
          setTimeout(() => {
            this.atsNameComputed = oldValue;
          }, 500);
        }
      },
    },
  },
  methods: {
    ...mapActions([
      "updateCurrentUserSettings",
    ]),
    onInput(key) {
      this.keyValid = false;
      if (key === "greenhouseApiKey") {
        this.atsGreenhouseKeyCheck.isEditing = true;
      } else if (key === "leverApiKey") {
        this.atsLeverKeyCheck.isEditing = true;
      }
    },
    atsStagesUpdated() {
      this.throttlePromptSettingsSaved();
    },
    saveSettings() {
      this.updateCurrentUserSettings();
      this.throttlePromptSettingsSaved();
    },
    throttlePromptSettingsSaved: _.throttle(function() {
      this.$notify({
        position: "bottom-right",
        message: "Your settings have been saved.",
        title: "Saved",
        type: "success",
      });
    }, 4000),
    cancelEditKey(key) {
      if (key === "greenhouseApiKey") {
        this.settings.greenhouseApiKey = this.atsGreenhouseKeyCheck.originalKey;
        this.atsGreenhouseKeyCheck.isEditing = false;
      } else if (key === "leverApiKey") {
        this.settings.leverApiKey = this.atsLeverKeyCheck.originalKey;
        this.atsLeverKeyCheck.isEditing = false;
      }
    },
    editKey(key) {
      if (key === "greenhouseApiKey") {
        this.atsGreenhouseKeyCheck.isEditing = true;
        this.atsGreenhouseKeyCheck.originalKey = this.settings.greenhouseApiKey;
      } else if (key === "leverApiKey") {
        this.atsLeverKeyCheck.isEditing = true;
        this.atsLeverKeyCheck.originalKey = this.settings.leverApiKey;
      }

      this.settings[key] = null;
    },
    async saveGreenhouseKey() {
      this.atsGreenhouseKeyCheck.isLoading = true;
      const { data } = await UsersApi.verifyGreenhouseKey(this.settings.greenhouseApiKey);
      const isSuccess = !data.issues;

      if (isSuccess) {
        this.keyValid = true;
        this.settings.leverApiKey = null;
        this.user.primaryTeamAtsName = this.atsName;
        await this.updateCurrentUserSettings({ greenhouseKeyChanged: true, leverKeyChanged: true });
        const { data } = await AtsApi.allStages();
        this.settings.availableAtsStages = data.availableAtsStages;
      }

      this.atsGreenhouseKeyCheck.description = isSuccess ? "Success" : data.issues;
      this.atsGreenhouseKeyCheck.success = isSuccess;
      this.atsGreenhouseKeyCheck.status = isSuccess ? "success" : "error";
      this.atsGreenhouseKeyCheck.isLoading = false;
      this.atsGreenhouseKeyCheck.isEditing = false;
    },
    async saveLeverKey() {
      this.atsLeverKeyCheck.isLoading = true;
      const { data } = await UsersApi.verifyLeverKey(this.settings.leverApiKey);
      const isSuccess = !data.issues;

      if (isSuccess) {
        this.keyValid = true;
        this.settings.greenhouseApiKey = null;
        this.user.primaryTeamAtsName = this.atsName;
        await this.updateCurrentUserSettings({ leverKeyChanged: true, greenhouseKeyChanged: true });
        const { data } = await AtsApi.allStages();
        this.settings.availableAtsStages = data.availableAtsStages;
      }

      this.atsLeverKeyCheck.description = isSuccess ? "Success" : data.issues;
      this.atsLeverKeyCheck.success = isSuccess;
      this.atsLeverKeyCheck.status = isSuccess ? "success" : "error";
      this.atsLeverKeyCheck.isLoading = false;
      this.atsLeverKeyCheck.isEditing = false;
    },
    // Harvest v3 (OAuth): kick off the "Connect Greenhouse" flow by redirecting
    // the browser to the Greenhouse authorize URL. The OAuth callback stores the
    // tokens server-side and redirects back here with a ?greenhouse= query param.
    async connectGreenhouse() {
      this.greenhouseV3Loading = true;
      try {
        const { data } = await UsersApi.greenhouseOauthUrl();
        window.location.href = data.url;
      } catch (e) {
        this.greenhouseV3Loading = false;
        this.greenhouseV3MessageType = "error";
        // The server crafts actionable copy for the cases it knows about — a team already
        // on another ATS (409, "contact support to switch") and an unconfigured OAuth app
        // (503). Show it; the generic line is only for failures it didn't name.
        this.greenhouseV3Message =
          e.response?.data?.error || "Greenhouse is not available to connect right now. Please try again later.";
      }
    },
    // Gated behind a confirmation: one stray click stops all Greenhouse syncing, and
    // reconnecting needs a Greenhouse login that can manage API credentials — which
    // whoever clicked may not have.
    async disconnectGreenhouseV3() {
      try {
        await this.$confirm(
          "Teamable will stop syncing with Greenhouse until someone reconnects it, which requires a Greenhouse login that can manage API credentials.",
          "Disconnect Greenhouse?",
          { confirmButtonText: "Disconnect", cancelButtonText: "Cancel", type: "warning" }
        );
      } catch (e) {
        return;
      }

      this.greenhouseV3Loading = true;
      try {
        await UsersApi.disconnectGreenhouse();
        this.settings.greenhouseV3Connected = false;
        // The team only still has an ATS if a v1 key remains; otherwise the stage
        // configuration below has nothing to configure and must stop showing.
        this.keyValid = !!this.settings.greenhouseApiKey;
        if (!this.keyValid) {
          this.user.primaryTeamAtsName = null;
        }
        this.greenhouseV3MessageType = "success";
        this.greenhouseV3Message = "Greenhouse has been disconnected.";
      } catch (e) {
        this.greenhouseV3MessageType = "error";
        this.greenhouseV3Message = "Could not disconnect Greenhouse right now. Please try again later.";
      } finally {
        this.greenhouseV3Loading = false;
      }
    },
    testGreenhouseKey() {
      this.atsGreenhouseKeyCheck.isLoading = true;
      UsersApi.testGreenhouseKey().then(({ data }) => {
        this.atsGreenhouseKeyCheck.isLoading = false;
        const isSuccess = !data.issues;
        this.atsGreenhouseKeyCheck.description = isSuccess ? "Success" : data.issues
        this.atsGreenhouseKeyCheck.success = isSuccess;
        this.atsGreenhouseKeyCheck.status = isSuccess ? "success" : "error";
      })
    },
    testLeverKey() {
      this.atsLeverKeyCheck.isLoading = true;
      UsersApi.testLeverKey().then(({ data }) => {
        this.atsLeverKeyCheck.isLoading = false;
        const isSuccess = !data.issues;
        this.atsLeverKeyCheck.description = isSuccess ? "Success" : data.issues
        this.atsLeverKeyCheck.success = isSuccess;
        this.atsLeverKeyCheck.status = isSuccess ? "success" : "error";
      });
    },
    submit() {
      if (this.atsName === "None") {
        this.user.primaryTeamAtsName = null;
        this.settings.greenhouseApiKey = null;
        this.settings.leverApiKey = null;

        this.updateCurrentUserSettings({ greenhouseKeyChanged: true, leverKeyChanged: true });
      }

      this.user.showAtsConnect = false;
      this.$emit("finish-connect");
    },
    resetAtsKeyCheck() {
      Object.assign(this.atsGreenhouseKeyCheck, this.$options.data.call(this).atsGreenhouseKeyCheck);
      Object.assign(this.atsLeverKeyCheck, this.$options.data.call(this).atsLeverKeyCheck);
    },
    confirmBackfill(setting) {
      this.$confirm(`Do you want this setting to apply to all of your team's jobs? This will not affect candidates already in ${this.user.primaryTeamAtsName}.`, "Apply to existing jobs?", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "info",
      }).then(() => {
        this.settings[setting] = true;
        this.saveSettings();
      }).catch(() => {
        this.settings[setting] = false;
        this.saveSettings();
      });
    },
    getMergeLinkToken() {
      loadMergeDevScript();

      UsersApi.mergeDevModalLinkToken().then(({ data }) => {
        initializeMergeDev(data.linkToken);
      })
    },
  },
  mounted() {
    if (!this.user.isTeamAdmin) {
      this.$router.replace({ name: "settings-root" });
      return;
    }

    this.hadExistingGreenhouseKey = !!this.settings.greenhouseApiKey;
    if (this.user.primaryTeamAtsName) {
      this.keyValid = true;
      this.atsName = this.user.primaryTeamAtsName;
    }

    if (this.settings.mergeDevApiKey || this.atsNameComputed === "None") {
      // linkToken retrieved from backend (Step 1)
      this.getMergeLinkToken();
    }

    // Surface the result of the Greenhouse OAuth round-trip (the callback
    // redirects back here with ?greenhouse=connected or ?greenhouse_error=...).
    const { greenhouse, greenhouse_error: greenhouseError } = this.$route.query;
    // A failed connect rolls the tokens back, so a fresh team lands here with no ATS set
    // — showGreenhouseTab would be false and the banner (which renders inside it) would
    // never show, leaving a silent ATS-selection screen after the whole consent trip.
    if ((greenhouse || greenhouseError) && !this.user.primaryTeamAtsName) {
      this.atsName = "Greenhouse";
    }
    if (greenhouseError) {
      this.greenhouseV3MessageType = "error";
      this.greenhouseV3Message = GREENHOUSE_OAUTH_ERRORS[greenhouseError] || GREENHOUSE_OAUTH_ERROR_FALLBACK;
    } else if (greenhouse === "connected") {
      this.greenhouseV3MessageType = "success";
      this.greenhouseV3Message = "Greenhouse connected.";
    }

    // Drop the params once read, or a refresh or bookmark of the post-connect URL
    // replays the same banner indefinitely.
    if (greenhouse || greenhouseError) {
      const query = { ...this.$route.query };
      delete query.greenhouse;
      delete query.greenhouse_error;
      this.$router.replace({ query });
    }
  },
};
